import { BaseApi } from './BaseApi';
import { SingaporeTime } from '../../utils/unified-date';

export type { DbResult, DbError } from './BaseApi';
import type { DbResult, DbError } from './BaseApi';

export interface UserRow {
  id: string;
  name?: string;
  email?: string;
  role?: string;
  is_active?: boolean;
  created_at?: string;
}

export interface UserUpdateFields {
  name?: string;
  email?: string;
  role?: string;
  is_active?: boolean;
}

/**
 * Queries on the `users` table used by the Users, Rosters, Tasks filters and Jobs pages.
 */
export class UserApi extends BaseApi {
  private static toResult<T>(response: { data: T | null; error: DbError | null }): DbResult<T> {
    return { data: response.data, error: response.error };
  }

  /** All users, optionally limited to active or deactivated ones */
  static async listUsers(
    status: 'all' | 'active' | 'deactivated' = 'all'
  ): Promise<DbResult<UserRow[]>> {
    let query = this.getClient()
      .from('users')
      .select('id, name, email, role, is_active, created_at')
      .order('name');

    if (status === 'active') {
      query = query.eq('is_active', true);
    } else if (status === 'deactivated') {
      query = query.eq('is_active', false);
    }

    return this.toResult<UserRow[]>(await query);
  }

  /** Update profile fields of one user (stamps updated_at) */
  static async updateUser(id: string, fields: UserUpdateFields): Promise<DbResult<null>> {
    const response = await this.getClient()
      .from('users')
      .update({ ...fields, updated_at: SingaporeTime.now() })
      .eq('id', id);
    return this.toResult<null>({ data: null, error: response.error });
  }

  /** Delete one user row and return the deleted rows (empty when nothing matched) */
  static async deleteUser(id: string): Promise<DbResult<{ id: string }[]>> {
    const response = await this.getClient().from('users').delete().eq('id', id).select();
    return this.toResult<{ id: string }[]>(response);
  }

  /** Users that already use this email address */
  static async findByEmail(email: string): Promise<DbResult<{ id: string }[]>> {
    const response = await this.getClient().from('users').select('id').eq('email', email);
    return this.toResult<{ id: string }[]>(response);
  }

  /** Role of one user */
  static async getRole(id: string): Promise<DbResult<{ role: string }>> {
    const response = await this.getClient().from('users').select('role').eq('id', id).single();
    return this.toResult<{ role: string }>(response);
  }

  /** id + name of every worker, sorted by name */
  static async listWorkerNames(): Promise<DbResult<{ id: string; name: string }[]>> {
    const response = await this.getClient()
      .from('users')
      .select('id, name')
      .eq('role', 'worker')
      .order('name');
    return this.toResult<{ id: string; name: string }[]>(response);
  }

  /** id, name and email of every active worker */
  static async listActiveWorkers(): Promise<
    DbResult<{ id: string; name: string; email?: string }[]>
  > {
    const response = await this.getClient()
      .from('users')
      .select('id, name, email')
      .eq('role', 'worker')
      .eq('is_active', true);
    return this.toResult<{ id: string; name: string; email?: string }[]>(response);
  }

  /** id, name, role and active flag of every worker and driver (active users first) */
  static async listWorkersAndDrivers(): Promise<
    DbResult<{ id: string; name: string; role: string; is_active: boolean }[]>
  > {
    const response = await this.getClient()
      .from('users')
      .select('id, name, role, is_active')
      .in('role', ['worker', 'driver'])
      .order('is_active', { ascending: false });
    return this.toResult<{ id: string; name: string; role: string; is_active: boolean }[]>(
      response
    );
  }

  // ---- self sign-up profile helpers (used by AuthContext) ----

  /** name, role and email of one user (single row) */
  static async getProfile(
    id: string
  ): Promise<DbResult<{ name: string; role: string; email: string }>> {
    const response = await this.getClient()
      .from('users')
      .select('name, role, email')
      .eq('id', id)
      .single();
    return this.toResult<{ name: string; role: string; email: string }>(response);
  }

  /** The id of a user, or null data when no such user exists (single row) */
  static async findById(id: string): Promise<DbResult<{ id: string }>> {
    const response = await this.getClient().from('users').select('id').eq('id', id).single();
    return this.toResult<{ id: string }>(response);
  }

  /** id and email of the user with this exact name (single row) */
  static async findByName(name: string): Promise<DbResult<{ id: string; email: string }>> {
    const response = await this.getClient()
      .from('users')
      .select('id, email')
      .eq('name', name)
      .single();
    return this.toResult<{ id: string; email: string }>(response);
  }

  /** Update name, email and role of an existing profile */
  static async updateProfile(
    id: string,
    fields: { name: string; email: string; role: string }
  ): Promise<DbResult<null>> {
    const { error } = await this.getClient().from('users').update(fields).eq('id', id);
    return { data: null, error };
  }

  /** Insert a new profile row */
  static async insertProfile(row: {
    id: string;
    name: string;
    email: string;
    role: string;
  }): Promise<DbResult<null>> {
    const { error } = await this.getClient().from('users').insert([row]);
    return { data: null, error };
  }
}
